import { Tag } from 'antd';
import { FilterFieldConfig } from 'common/dynamic-filter-form';
import { dateTimeCell, dateCell, linkCell } from 'utils/cell-formatter';

// Thresholds are for list-page triage only (flag for a closer look), not a
// judgment that the data is wrong -- a stock can genuinely be this cheap.
const DISCOUNT_PCT_WARN_THRESHOLD = 60; // |discount_pct| beyond this is unusual enough to double check
// Same rule the detail page's methodDisagreement banner uses (see detail.tsx)
// -- kept in step manually since the two files don't share a constants module.
const METHOD_DISAGREEMENT_THRESHOLD_PCT = 100;

const discountPctCell = ({ getValue }) => {
  const value = getValue();
  if (value === null || value === undefined) return <span className="text-gray-400">-</span>;
  const extreme = Math.abs(value) >= DISCOUNT_PCT_WARN_THRESHOLD;
  return (
    <div className="text-right">
      <span className={extreme ? 'font-medium text-red-600' : ''}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
};

const snowflakeCell = ({ row }) => {
  const score = row.original.snowflake_score;
  const possible = row.original.snowflake_possible;
  if (score === null || score === undefined || !possible) {
    return <span className="text-gray-400">-</span>;
  }
  const ratio = score / possible;
  const color = ratio >= 0.6 ? 'green' : ratio >= 0.3 ? 'orange' : 'red';
  return (
    <div className="text-right">
      <Tag color={color}>
        {score}/{possible}
      </Tag>
    </div>
  );
};

// Flags when the DCF estimate and the blended fair price disagree by more
// than the threshold -- the same signal detail.tsx computes per-symbol, but
// only visible there today, so finding "which of ~2000 symbols need a closer
// look" required opening each one. Surfacing it here is the whole point of a
// triage list.
const methodDisagreementCell = ({ row }) => {
  const fairPrice = row.original.fair_price;
  const dcfFairPrice = row.original.dcf_fair_price;
  if (!fairPrice || !dcfFairPrice) return <span className="text-gray-400">-</span>;
  const max = Math.max(fairPrice, dcfFairPrice);
  const min = Math.min(fairPrice, dcfFairPrice);
  const spreadPct = ((max - min) / min) * 100;
  if (spreadPct < METHOD_DISAGREEMENT_THRESHOLD_PCT) return <span className="text-gray-400">-</span>;
  return (
    <div className="text-right">
      <Tag color="volcano">{spreadPct.toFixed(0)}%</Tag>
    </div>
  );
};

export const columnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) =>
      linkCell({ getValue, path: '/catalog/stock-fundamentals' }),
  },
  {
    accessorKey: 'symbol',
    header: 'Symbol',
  },
  {
    accessorKey: 'excluded_from_app',
    header: 'Excluded from app',
    meta: {
      className: 'text-center',
    },
    cell: ({ getValue }) =>
      getValue() ? <Tag color="red">Excluded</Tag> : <Tag color="green">Visible</Tag>,
  },
  {
    accessorKey: 'discount_pct',
    header: 'Discount %',
    meta: { className: 'text-right' },
    cell: discountPctCell,
  },
  {
    accessorKey: 'snowflake_score',
    header: 'Snowflake',
    meta: { className: 'text-right' },
    cell: snowflakeCell,
  },
  {
    accessorKey: 'dcf_fair_price',
    header: 'Method disagreement',
    meta: { className: 'text-right' },
    cell: methodDisagreementCell,
  },
  {
    accessorKey: 'priced_at',
    header: 'Priced at',
    meta: { className: 'text-right' },
    cell: dateCell,
  },
  {
    // generated_at is when the model produced the file, not when investcore
    // synced it.
    accessorKey: 'generated_at',
    header: 'Model run',
    meta: { className: 'text-right' },
    cell: dateTimeCell,
  },
];

// Backend needs no filter-specific wiring for any of these: the generic
// admin query layer (pkg/zen HttpHandler.GetGormFilters) turns any query
// param matching a real column into a `WHERE column op value` clause on its
// own -- __gte/__lte parse into >=/<= (see parseOperator in handler.go).
// Confirmed against a real DB, not just by reading that code (see
// stock_fundamental_test.go: TestGetAll_FiltersByExcludedFromApp,
// TestGetAll_FiltersBySnowflakeScoreRange).
export const filterConfig: FilterFieldConfig[] = [
  {
    name: 'excluded_from_app',
    type: 'select',
    label: 'Excluded from app',
    placeholder: 'All',
    minWidth: '160px',
    options: [
      { value: 'true', label: 'Excluded' },
      { value: 'false', label: 'Visible' },
    ],
    defaultValue: 'all',
  },
  {
    name: 'snowflake_score__gte',
    type: 'text',
    label: 'Snowflake score min',
    placeholder: 'e.g. 0',
    minWidth: '140px',
  },
  {
    name: 'snowflake_score__lte',
    type: 'text',
    label: 'Snowflake score max',
    placeholder: 'e.g. 30',
    minWidth: '140px',
  },
];
